import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';

const LivraisonsMagasinier = () => {
  const navigate = useNavigate();
  const [livraisons, setLivraisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedLivraison, setSelectedLivraison] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    evenement_id: '',
    magasinier_id: '',
    transport_inclus: false,
    km_avant: '',
    km_apres: '',
    date_livraison: new Date().toISOString().slice(0, 16),
    date_retour: ''
  });
  const [evenementsDisponibles, setEvenementsDisponibles] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState('');

  useEffect(() => {
    chargerLivraisons();
    chargerEvenements();
  }, []);

  const chargerLivraisons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/livraisons`, config);
      setLivraisons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement livraisons:', error);
      setMessage({
        type: 'error',
        text: error.response?.status === 403 
          ? 'Accès refusé. Vérifiez vos permissions de magasinier.' 
          : 'Erreur lors du chargement des livraisons'
      });
      setLivraisons([]);
    } finally {
      setLoading(false);
    }
  };

  const chargerEvenements = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/evenements`, config);
      const evenementsValides = Array.isArray(response.data) 
        ? response.data.filter(e => e.statut === 'valide')
        : [];
      setEvenementsDisponibles(evenementsValides);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
    }
  };

  const handleOpenCreateDialog = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      setMessage({
        type: 'error',
        text: 'Utilisateur non connecté'
      });
      return;
    }

    setFormData({
      evenement_id: '',
      magasinier_id: user.id,
      transport_inclus: false,
      km_avant: '',
      km_apres: '',
      date_livraison: new Date().toISOString().slice(0, 16),
      date_retour: ''
    });
    setDialogMode('create');
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (livraison) => {
    setSelectedLivraison(livraison);
    setFormData({
      evenement_id: livraison.evenement_id,
      magasinier_id: livraison.magasinier_id,
      transport_inclus: livraison.transport_inclus,
      km_avant: livraison.km_avant || '',
      km_apres: livraison.km_apres || '',
      date_livraison: livraison.date_livraison ? new Date(livraison.date_livraison).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
      date_retour: livraison.date_retour ? new Date(livraison.date_retour).toISOString().slice(0, 16) : ''
    });
    setDialogMode('edit');
    setOpenDialog(true);
  };

  const handleOpenViewDialog = (livraison) => {
    setSelectedLivraison(livraison);
    setDialogMode('view');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLivraison(null);
    setFormData({
      evenement_id: '',
      magasinier_id: '',
      transport_inclus: false,
      km_avant: '',
      km_apres: '',
      date_livraison: new Date().toISOString().slice(0, 16),
      date_retour: ''
    });
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.evenement_id) {
      setMessage({
        type: 'error',
        text: 'Veuillez sélectionner un événement'
      });
      return;
    }

    if (!formData.date_livraison) {
      setMessage({
        type: 'error',
        text: 'La date de livraison est requise'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const data = {
        ...formData,
        km_avant: formData.km_avant ? parseInt(formData.km_avant) : null,
        km_apres: formData.km_apres ? parseInt(formData.km_apres) : null,
        date_retour: formData.date_retour || null
      };

      if (dialogMode === 'create') {
        await axios.post(`${API_URL}/livraisons`, data, config);
        setMessage({
          type: 'success',
          text: 'Livraison créée avec succès'
        });
      } else {
        await axios.put(`${API_URL}/livraisons/${selectedLivraison.id}`, data, config);
        setMessage({
          type: 'success',
          text: 'Livraison mise à jour avec succès'
        });
      }

      chargerLivraisons();
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur sauvegarde livraison:', error);
      let errorMessage = 'Erreur lors de la sauvegarde';
      
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette livraison ? Cette action est irréversible.')) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(`${API_URL}/livraisons/${id}`, config);
      
      setMessage({
        type: 'success',
        text: 'Livraison supprimée avec succès'
      });
      
      chargerLivraisons();
    } catch (error) {
      console.error('Erreur suppression:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la suppression'
      });
    }
  };

  const handleGestionRetour = (livraison) => {
    navigate(`/magasinier/retours/${livraison.id}`);
  };

  const handleGestionLivraisonMateriels = (livraison) => {
    navigate(`/magasinier/livraison-materiels/${livraison.id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatutBadge = (livraison) => {
    if (!livraison.date_retour) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          En cours
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        Terminée
      </span>
    );
  };

  const livraisonsFiltrees = livraisons.filter(livraison => {
    if (filtreStatut === 'en_cours' && livraison.date_retour) return false;
    if (filtreStatut === 'termine' && !livraison.date_retour) return false;
    return true;
  });

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const stats = {
    total: livraisons.length,
    enCours: livraisons.filter(l => !l.date_retour).length,
    terminees: livraisons.filter(l => l.date_retour).length,
    avecTransport: livraisons.filter(l => l.transport_inclus).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des livraisons...</p>
        </div>
      </div>
    );
  }

  const paginatedLivraisons = livraisonsFiltrees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Livraisons</h1>
              <p className="text-gray-600 mt-1">Interface Magasinier - Gestion des livraisons et retours</p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
              >
                Tableau de bord
              </button>
              <button
                onClick={handleOpenCreateDialog}
                disabled={evenementsDisponibles.length === 0}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  evenementsDisponibles.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Nouvelle Livraison
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Message d'avertissement */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
            message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
            'bg-blue-50 border border-blue-200 text-blue-700'
          }`}>
            <div className="flex items-center">
              <svg className={`w-5 h-5 mr-3 ${
                message.type === 'error' ? 'text-red-500' :
                message.type === 'success' ? 'text-green-500' : 'text-blue-500'
              }`} fill="currentColor" viewBox="0 0 20 20">
                {message.type === 'error' ? (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                ) : message.type === 'success' ? (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                )}
              </svg>
              <span>{message.text}</span>
            </div>
          </div>
        )}

        {/* Avertissement événements */}
        {evenementsDisponibles.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="text-sm font-medium text-blue-800">Information</div>
                <div className="text-sm text-blue-700 mt-1">
                  Aucun événement validé disponible pour créer une livraison.
                  Les événements doivent d'abord être validés par le validateur.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border rounded-lg p-6">
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500 mt-1">Total Livraisons</div>
          </div>
          <div className="bg-white border rounded-lg p-6">
            <div className="text-3xl font-bold text-yellow-600">{stats.enCours}</div>
            <div className="text-sm text-gray-500 mt-1">En cours</div>
          </div>
          <div className="bg-white border rounded-lg p-6">
            <div className="text-3xl font-bold text-green-600">{stats.terminees}</div>
            <div className="text-sm text-gray-500 mt-1">Terminées</div>
          </div>
          <div className="bg-white border rounded-lg p-6">
            <div className="text-3xl font-bold text-blue-600">{stats.avecTransport}</div>
            <div className="text-sm text-gray-500 mt-1">Avec transport</div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white border rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrer par statut
              </label>
              <select
                value={filtreStatut}
                onChange={(e) => {
                  setFiltreStatut(e.target.value);
                  setPage(0);
                }}
                className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tous les statuts</option>
                <option value="en_cours">En cours</option>
                <option value="termine">Terminées</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={chargerLivraisons}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
              >
                Actualiser
              </button>
              <button
                onClick={chargerEvenements}
                className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Vérifier événements
              </button>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Liste des livraisons</h2>
            <p className="text-sm text-gray-500 mt-1">
              {livraisonsFiltrees.length} livraison{livraisonsFiltrees.length > 1 ? 's' : ''}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° Événement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Livraison
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Retour
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transport
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedLivraisons.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        {livraisons.length === 0 
                          ? 'Aucune livraison trouvée. Créez votre première livraison.' 
                          : 'Aucune livraison ne correspond aux critères de filtre.'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedLivraisons.map((livraison) => (
                    <tr key={livraison.id} className="hover: transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-gray-900">#{livraison.id}</div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatutBadge(livraison)}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                           Événement #{livraison.evenement?.id}
                          </div>
                          
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{formatDate(livraison.date_livraison)}</div>
                      </td>
                      <td className="px-6 py-4">
                        {livraison.date_retour ? (
                          <div className="text-sm text-gray-900">{formatDate(livraison.date_retour)}</div>
                        ) : (
                          <div className="text-sm text-gray-500">En attente</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {livraison.transport_inclus ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Oui
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">Non</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleOpenViewDialog(livraison)}
                            className="p-1 text-blue-600 hover:text-blue-800"
                            title="Voir détails"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleOpenEditDialog(livraison)}
                            className="p-1 text-green-600 hover:text-green-800"
                            title="Modifier"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleGestionLivraisonMateriels(livraison)}
                            className="p-1 text-orange-600 hover:text-orange-800"
                            title="Gérer matériels"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleGestionRetour(livraison)}
                            className="p-1 text-purple-600 hover:text-purple-800"
                            title="Gérer retour"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(livraison.id)}
                            className="p-1 text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Affichage de {Math.min(page * rowsPerPage + 1, livraisonsFiltrees.length)} à {Math.min((page + 1) * rowsPerPage, livraisonsFiltrees.length)} sur {livraisonsFiltrees.length} livraisons
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleChangePage(page - 1)}
                    disabled={page === 0}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => handleChangePage(page + 1)}
                    disabled={(page + 1) * rowsPerPage >= livraisonsFiltrees.length}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {dialogMode === 'create' ? 'Nouvelle Livraison' : 
                 dialogMode === 'edit' ? 'Modifier Livraison' : 'Détails Livraison'}
                {selectedLivraison && ` #${selectedLivraison.id}`}
              </h2>
            </div>

            <div className="p-6">
              {dialogMode === 'view' ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Événement</h3>
                    <p className="mt-1 text-gray-900">
                      #{selectedLivraison.evenement?.id} - {selectedLivraison.evenement?.beneficiaire?.nom}
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Statut</h3>
                    <div className="mt-1">{getStatutBadge(selectedLivraison)}</div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Dates</h3>
                    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Livraison</p>
                        <p className="text-gray-900">{formatDateTime(selectedLivraison.date_livraison)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Retour</p>
                        <p className="text-gray-900">
                          {selectedLivraison.date_retour 
                            ? formatDateTime(selectedLivraison.date_retour) 
                            : 'En attente'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {selectedLivraison.transport_inclus && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">Kilométrage</h3>
                      <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Avant</p>
                          <p className="text-gray-900">{selectedLivraison.km_avant || 'N/A'} km</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Après</p>
                          <p className="text-gray-900">{selectedLivraison.km_apres || 'N/A'} km</p>
                        </div>
                        {selectedLivraison.km_avant && selectedLivraison.km_apres && (
                          <div>
                            <p className="text-sm text-gray-500">Distance</p>
                            <p className="text-gray-900">{selectedLivraison.km_apres - selectedLivraison.km_avant} km</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Événement *
                    </label>
                    <select
                      name="evenement_id"
                      value={formData.evenement_id}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={dialogMode === 'edit'}
                      required
                    >
                      <option value="">Sélectionner un événement</option>
                      {evenementsDisponibles.map(event => (
                        <option key={event.id} value={event.id}>
                          #{event.id} - {event.beneficiaire?.nom || 'Sans bénéficiaire'} - {event.type}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Seuls les événements validés sont disponibles
                    </p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="transport_inclus"
                      checked={formData.transport_inclus}
                      onChange={handleFormChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Transport inclus
                    </label>
                  </div>

                  {formData.transport_inclus && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kilométrage avant livraison
                        </label>
                        <input
                          type="number"
                          name="km_avant"
                          value={formData.km_avant}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kilométrage après livraison
                        </label>
                        <input
                          type="number"
                          name="km_apres"
                          value={formData.km_apres}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          min="0"
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date et heure de livraison *
                      </label>
                      <input
                        type="datetime-local"
                        name="date_livraison"
                        value={formData.date_livraison}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date et heure de retour
                      </label>
                      <input
                        type="datetime-local"
                        name="date_retour"
                        value={formData.date_retour}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
              >
                {dialogMode === 'view' ? 'Fermer' : 'Annuler'}
              </button>
              {dialogMode !== 'view' && (
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {dialogMode === 'create' ? 'Créer' : 'Mettre à jour'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LivraisonsMagasinier;